import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/BillHistoryPage.css';

// Components
import BillFilterPanel from '../components/bill-history/BillFilterPanel';
import BillStatsBar from '../components/bill-history/BillStatsBar';
import BillTable from '../components/bill-history/BillTable';
import InvoicePreview from '../components/InvoicePreview';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const BillHistoryPage = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  // Filters State
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    paymentMode: '',
    paymentMode: '',
    source: '',
    showTrash: false
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selected Bill for View Modal
  const [selectedBill, setSelectedBill] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);

  // Legacy Modal State
  const [showLegacyModal, setShowLegacyModal] = useState(false);
  const [legacyForm, setLegacyForm] = useState({
    customerName: '', customerPhone: '', billDate: '', amount: '', paymentStatus: 'Unpaid', notes: ''
  });
  const [legacyFile, setLegacyFile] = useState(null);

  // Customer State for Search
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  useEffect(() => {
    fetchBills();
  }, [filters.showTrash]);

  useEffect(() => {
    fetchBills();
    fetchCustomers();

    // Check for navigation state
    if (location.state?.filterPhone) {
      setFilters(prev => ({ ...prev, search: location.state.filterPhone }));
    }
  }, [location.state]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get('/api/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error("Failed to fetch customers", err);
    }
  };

  const fetchBills = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/bills?trash=${filters.showTrash ? 'true' : 'false'}`);
      setBills(res.data);
    } catch (err) {
      console.error("Failed to fetch bills", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setLegacyFile(e.target.files[0]);
  };

  const handleLegacySubmit = async (e) => {
    e.preventDefault();
    if (!legacyFile) return alert("Please upload a file");

    const formData = new FormData();
    // Append Text Fields FIRST so Multer can read them for filename
    Object.keys(legacyForm).forEach(key => formData.append(key, legacyForm[key]));
    formData.append('file', legacyFile);

    try {
      await axios.post('/api/legacy-bills', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert("Legacy Bill Uploaded Successfully");
      setShowLegacyModal(false);
      setLegacyForm({ customerName: '', customerPhone: '', billDate: '', amount: '', paymentStatus: 'Unpaid', notes: '' });
      setLegacyFile(null);
      fetchBills(); // Refresh list
    } catch (err) {
      alert('Failed to upload bill');
    }
  };

  // --- Edit Legacy/System Logic ---
  const [editModal, setEditModal] = useState({
    show: false,
    bill: null,
    form: {
      paymentStatus: 'Unpaid',
      paymentMode: '',
      paymentDate: '',
      notes: '',
      receivedAmount: '' // New Field for partial steps
    },
    stats: null // Initialize stats
  });

  const handleEditClick = (bill) => {
    // Standardize IDs
    const total = parseFloat(bill.totalAmount || bill.amount || 0);
    const paid = parseFloat(bill.paymentDetails?.advance || bill.paidAmount || 0);
    const balance = parseFloat(bill.paymentDetails?.balance ?? (total - paid));

    setEditModal({
      show: true,
      bill: bill,
      form: {
        paymentStatus: bill.paymentStatus || bill.paymentDetails?.status || 'Unpaid',
        paymentMode: bill.paymentMode || bill.paymentDetails?.mode || '',
        paymentDate: bill.paymentDate ? new Date(bill.paymentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        notes: bill.notes || '',
        receivedAmount: '' // Reset
      },
      stats: { total, paid, balance } // Store initial stats
    });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    const { form, bill, stats } = editModal;
    const isSystem = bill.source !== 'LEGACY';
    const received = parseFloat(form.receivedAmount) || 0;

    // Logic: If received > 0, we are updating the balance
    let newStatus = form.paymentStatus;
    let newPaid = stats.paid + received;
    let newBalance = stats.total - newPaid;

    // Auto-update status if fully paid
    if (newBalance <= 0) {
      newBalance = 0;
      newStatus = 'Paid';
    } else if (received > 0 && newStatus === 'Unpaid') {
      newStatus = 'Partial';
    }

    try {
      if (isSystem) {
        // System update
        const payload = {
          paymentDetails: {
            ...bill.paymentDetails,
            status: newStatus,
            mode: form.paymentMode,
            advance: newPaid,
            balance: newBalance
          },
          notes: form.notes
        };

        // Add History Entry if money was received
        if (received > 0) {
          payload.paymentHistoryEntry = {
            date: form.paymentDate || new Date(),
            amount: received,
            mode: form.paymentMode,
            note: form.notes || 'Partial Payment'
          };
        }

        await axios.put(`/api/bills/${bill._id}`, payload);
      } else {
        // Legacy Update
        const payload = {
          ...form,
          paymentStatus: newStatus,
          paidAmount: newPaid,
          // Legacy schema doesn't have balance, it's derived.
          amount: stats.total // Ensure amount stays put
        };
        await axios.put(`/api/legacy-bills/${bill._id || bill.id}`, payload);
      }

      alert("Bill Updated Successfully");
      setEditModal({ show: false, bill: null, form: {}, stats: null });
      fetchBills();
    } catch (err) {
      console.error(err);
      alert("Failed to update bill");
    }
  };


  // --- Filtering Logic (Memoized) ---
  const filteredBills = useMemo(() => {
    return bills.filter(bill => {
      // 0. Source Filter
      if (filters.source && (bill.source || 'SYSTEM') !== filters.source) return false;

      // 1. Search (Global)
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        bill.billNumber?.toLowerCase().includes(searchLower) ||
        bill.customerName?.toLowerCase().includes(searchLower) || // Legacy field
        bill.customerPhone?.includes(searchLower) || // Legacy field
        bill.customer?.firstName?.toLowerCase().includes(searchLower) ||
        bill.customer?.lastName?.toLowerCase().includes(searchLower) ||
        bill.customer?.phone?.includes(searchLower);

      if (!matchesSearch) return false;

      // 2. Date Range
      const billDate = new Date(bill.billDate || bill.createdAt);
      if (filters.dateFrom) {
        if (billDate < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo) {
        const nextDay = new Date(filters.dateTo);
        nextDay.setDate(nextDay.getDate() + 1);
        if (billDate >= nextDay) return false;
      }

      // 3. Status
      const status = bill.paymentStatus || bill.paymentDetails?.status;
      if (filters.status && status !== filters.status) return false;

      // 4. Mode
      const mode = bill.paymentMode || bill.paymentDetails?.mode;
      if (filters.paymentMode && mode !== filters.paymentMode) return false;

      return true;
    });
  }, [bills, filters]);

  // --- Pagination Logic ---
  const paginatedBills = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredBills.slice(startIndex, startIndex + pageSize);
  }, [filteredBills, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredBills.length / pageSize);

  // --- Actions ---
  const handleResetFilters = () => {
    setFilters({ search: '', dateFrom: '', dateTo: '', status: '', paymentMode: '', source: '' });
    setCurrentPage(1);
  };

  const handleDownloadPDF = (bill) => {
    setSelectedBill(bill);
    setAutoPrint(true);
  };

  const handleExportCSV = () => {
    // Basic CSV Export
    const headers = ["Bill No", "Date", "Customer", "Phone", "Status", "Mode", "Amount"];
    const rows = filteredBills.map(b => {
      const date = new Date(b.billDate || b.createdAt).toLocaleDateString();
      const name = b.customerName || `${b.customer?.firstName || ''} ${b.customer?.lastName || ''}`.trim() || 'Unknown';
      const phone = b.customerPhone || b.customer?.phone || '-';
      const status = b.paymentStatus || b.paymentDetails?.status || 'Pending';
      const mode = b.paymentMode || b.paymentDetails?.mode || '-';
      const amount = b.amount || b.totalAmount || 0;

      return [
        b.billNumber,
        date,
        name,
        phone,
        status,
        mode,
        amount
      ];
    });

    const csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `bills_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteBill = async (bill) => {
    const action = bill.isDeleted ? "restore" : "delete";
    if (!window.confirm(`Are you sure you want to ${action} this bill?`)) return;

    try {
      await axios.delete(`/api/bills/${bill._id || bill.id}?source=${bill.source}`);
      fetchBills();
    } catch (err) {
      console.error(err);
      alert("Failed to update bill status");
    }
  };

  return (
    <div className="bill-history-container">
      {/* Header & Stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => window.location.href = '/billing'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              color: '#555',
              borderRadius: '50%',
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = '#eee'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
            title="Back to Billing"
          >
            <ArrowBackIcon />
          </button>
          <div>
            <h2 style={{ margin: 0, color: '#333' }}>Bill History</h2>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>Manage and view all customer invoices</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className={`btn-secondary btn-action ${filters.showTrash ? 'active-trash' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, showTrash: !prev.showTrash }))}
            style={{
              backgroundColor: filters.showTrash ? '#d32f2f' : '#f5f5f5',
              color: filters.showTrash ? '#fff' : '#333',
              border: '1px solid #ddd'
            }}
          >
            {filters.showTrash ? 'Hide Trash' : '🗑️ Trash'}
          </button>
          <button className="btn-secondary btn-action" onClick={() => setShowLegacyModal(true)} style={{ backgroundColor: '#ff9800', borderColor: '#f57c00' }}>
            <UploadFileIcon style={{ fontSize: '16px', marginRight: '4px', verticalAlign: 'text-bottom' }} /> Upload Old Bill
          </button>
          <button className="btn-secondary btn-action" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      <BillStatsBar bills={filteredBills} />

      {/* Filters */}
      <BillFilterPanel
        filters={filters}
        onFilterChange={setFilters}
        onReset={handleResetFilters}
      />

      {/* Table */}
      <BillTable
        bills={paginatedBills}
        onView={(bill) => {
          if (bill.source === 'LEGACY' && bill.filePath) {
            // Open file in new tab
            window.open(`http://localhost:5050/${bill.filePath}`, '_blank');
          } else {
            setSelectedBill(bill);
          }
        }}
        onEdit={handleEditClick}
        onDownloadPDF={handleDownloadPDF}
        onDelete={handleDeleteBill}
      />

      {/* Pagination Bar */}
      <div className="pagination-bar" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span className="page-info">
            Showing {filteredBills.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredBills.length)} of {filteredBills.length}
          </span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            style={{ padding: '4px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          >
            Previous
          </button>
          <span style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 'bold' }}>{currentPage} / {totalPages || 1}</span>
          <button
            className="page-btn"
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          >
            Next
          </button>
        </div>
      </div>

      {/* View Modal (Enhanced) */}
      {selectedBill && (
        <div className="modal-overlay" onClick={() => { setSelectedBill(null); setAutoPrint(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <InvoicePreview
              bill={selectedBill}
              autoPrint={autoPrint} // Pass prop
              onClose={() => { setSelectedBill(null); setAutoPrint(false); }}
              onPrint={() => window.print()}
            />
          </div>
        </div>
      )}

      {/* Edit Legacy Modal */}
      {editModal.show && (
        <div className="modal-overlay" onClick={() => setEditModal({ ...editModal, show: false })}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '400px' }}>
            <h3>Edit Legacy Bill</h3>
            <form onSubmit={handleUpdateSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Stats Summary */}
              {editModal.stats && (
                <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', border: '1px solid #eee' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Total Bill</div>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>₹{editModal.stats.total.toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Paid So Far</div>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#2e7d32' }}>₹{editModal.stats.paid.toLocaleString()}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Balance Due</div>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#d32f2f' }}>₹{editModal.stats.balance.toLocaleString()}</div>
                  </div>
                </div>
              )}

              <div className="filter-group">
                <label>Update Status</label>
                <select className="filter-control"
                  value={editModal.form.paymentStatus}
                  onChange={e => setEditModal(prev => ({ ...prev, form: { ...prev.form, paymentStatus: e.target.value } }))}
                >
                  <option value="Paid">Mark as Fully Paid</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Partial">Partial Payment</option>
                </select>
              </div>

              {/* Payment History Log */}
              {editModal.bill?.paymentHistory?.length > 0 && (
                <div style={{ background: '#eef', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
                  <strong>Payment History:</strong>
                  <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
                    {editModal.bill.paymentHistory.map((h, i) => (
                      <li key={i}>
                        {new Date(h.date).toLocaleDateString()}: <strong>₹{h.amount}</strong> ({h.mode}) - {h.note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {editModal.form.paymentStatus === 'Partial' && (
                <div className="filter-group" style={{ background: '#fff3e0', padding: '10px', borderRadius: '4px', border: '1px solid #ffe0b2' }}>
                  <label style={{ color: '#e65100' }}>Add Received Amount (+)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold', color: '#e65100' }}>₹</span>
                    <input type="number" className="filter-control"
                      placeholder="Enter amount received now"
                      value={editModal.form.receivedAmount}
                      onChange={e => setEditModal(prev => ({ ...prev, form: { ...prev.form, receivedAmount: e.target.value } }))}
                      style={{
                        flex: 1,
                        borderColor: (parseFloat(editModal.form.receivedAmount) > editModal.stats?.balance) ? 'red' : '#ffcc80',
                        color: (parseFloat(editModal.form.receivedAmount) > editModal.stats?.balance) ? 'red' : 'inherit'
                      }}
                    />
                  </div>

                  {/* Validation Error */}
                  {parseFloat(editModal.form.receivedAmount) > editModal.stats?.balance && (
                    <div style={{ fontSize: '11px', color: 'red', marginTop: '4px', fontWeight: 'bold' }}>
                      ⚠️ Amount exceeds pending balance (₹{editModal.stats.balance.toLocaleString()})
                    </div>
                  )}

                  {editModal.form.receivedAmount > 0 && parseFloat(editModal.form.receivedAmount) <= editModal.stats?.balance && editModal.stats && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '6px', textAlign: 'right', borderTop: '1px dashed #ccc', paddingTop: '4px' }}>
                      New Balance will be: <strong style={{ color: '#d32f2f' }}>₹{Math.max(0, editModal.stats.balance - (parseFloat(editModal.form.receivedAmount) || 0)).toLocaleString()}</strong>
                    </div>
                  )}
                </div>
              )}

              {(editModal.form.paymentStatus === 'Paid' || editModal.form.paymentStatus === 'Partial') && (
                <>
                  <div className="filter-group">
                    <label>Last Payment Date</label>
                    <input type="date" className="filter-control"
                      value={editModal.form.paymentDate}
                      onChange={e => setEditModal(prev => ({ ...prev, form: { ...prev.form, paymentDate: e.target.value } }))}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Payment Mode</label>
                    <select className="filter-control"
                      value={editModal.form.paymentMode}
                      onChange={e => setEditModal(prev => ({ ...prev, form: { ...prev.form, paymentMode: e.target.value } }))}
                    >
                      <option value="">Select Mode</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Card">Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </select>
                  </div>
                </>
              )}

              <div className="filter-group">
                <label>Notes</label>
                <textarea className="filter-control" rows="3"
                  value={editModal.form.notes}
                  onChange={e => setEditModal({ ...editModal, form: { ...editModal.form, notes: e.target.value } })}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                <button type="button" className="btn-secondary btn-action" style={{ background: '#ccc' }} onClick={() => setEditModal({ ...editModal, show: false })}>Cancel</button>
                <button
                  type="submit"
                  className="btn-secondary btn-action"
                  disabled={editModal.form.paymentStatus === 'Partial' && parseFloat(editModal.form.receivedAmount) > editModal.stats?.balance}
                  style={{
                    opacity: (editModal.form.paymentStatus === 'Partial' && parseFloat(editModal.form.receivedAmount) > editModal.stats?.balance) ? 0.5 : 1,
                    cursor: (editModal.form.paymentStatus === 'Partial' && parseFloat(editModal.form.receivedAmount) > editModal.stats?.balance) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Update Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Old Bill Modal (Refined) */}
      {showLegacyModal && (
        <div className="modal-overlay" onClick={() => setShowLegacyModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '450px', padding: '20px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Upload Old Bill</h3>
            <form onSubmit={handleLegacySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {/* Customer Section */}
              <div style={{ backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '4px' }}>CUSTOMER</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="filter-control"
                    placeholder="Search Customer (Name / Phone)"
                    value={legacyForm.customerName}
                    onChange={e => {
                      setLegacyForm({ ...legacyForm, customerName: e.target.value });
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    required
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                  {showCustomerDropdown && (legacyForm.customerName || customerSearch) && (
                    <div className="dropdown" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {customers.filter(c =>
                        c.firstName.toLowerCase().includes(legacyForm.customerName.toLowerCase()) ||
                        c.phone.includes(legacyForm.customerName)
                      ).map(customer => (
                        <div key={customer._id} className="dropdown-item" onClick={() => {
                          setLegacyForm({
                            ...legacyForm,
                            customerName: `${customer.firstName} ${customer.lastName}`,
                            customerPhone: customer.phone
                          });
                          setShowCustomerDropdown(false);
                        }}>
                          {customer.firstName} {customer.lastName} ({customer.phone})
                        </div>
                      ))}
                      <div className="dropdown-item" style={{ color: '#007bff', fontWeight: 'bold' }} onClick={() => setShowCustomerDropdown(false)}>
                        + Add "{legacyForm.customerName}" as new
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666' }}>PHONE</label>
                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}>
                    <span style={{ padding: '8px', background: '#eee', borderRight: '1px solid #ddd', fontSize: '13px', color: '#666' }}>🇮🇳 +91</span>
                    <input
                      className="no-border-input"
                      placeholder="9876543210"
                      value={legacyForm.customerPhone}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setLegacyForm({ ...legacyForm, customerPhone: val });
                      }}
                      required
                      style={{ border: 'none', padding: '8px', width: '100%', outline: 'none' }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666' }}>BILL DATE</label>
                  <input type="date" className="filter-control" required
                    value={legacyForm.billDate} onChange={e => setLegacyForm({ ...legacyForm, billDate: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Bill Details */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666' }}>AMOUNT</label>
                  <input type="number" className="filter-control" placeholder="0.00"
                    value={legacyForm.amount} onChange={e => setLegacyForm({ ...legacyForm, amount: e.target.value })}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>For reporting only.</div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#666' }}>STATUS</label>
                  <select className="filter-control" value={legacyForm.paymentStatus} onChange={e => setLegacyForm({ ...legacyForm, paymentStatus: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }}>
                    <option value="Unpaid">Pending (Default)</option>
                    <option value="Paid">Paid</option>
                    <option value="Partial">Partial</option>
                  </select>
                </div>
              </div>

              {/* File Upload (Styled) */}
              <div
                style={{
                  border: '2px dashed #ccc',
                  padding: '20px',
                  borderRadius: '6px',
                  textAlign: 'center',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onClick={() => document.getElementById('legacy-file-input').click()}
              >
                <input
                  id="legacy-file-input"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  required
                />
                <UploadFileIcon style={{ fontSize: '32px', color: '#999', marginBottom: '8px' }} />
                {legacyFile ? (
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>{legacyFile.name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{(legacyFile.size / 1024).toFixed(1)} KB</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ color: '#007bff', fontWeight: '500' }}>Click to Upload Bill</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>PDF or Image (Max 10MB)</div>
                    <div style={{ fontSize: '11px', color: '#999', fontStyle: 'italic', marginTop: '4px' }}>Name will be auto-generated</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                <button type="button" className="btn-secondary btn-action" style={{ background: '#f5f5f5', color: '#333', border: '1px solid #ddd' }} onClick={() => setShowLegacyModal(false)}>Cancel</button>
                <button type="submit" className="btn-secondary btn-action" style={{ backgroundColor: '#FFD700', color: '#000', borderColor: '#DAA520', fontWeight: 'bold' }}>Upload Bill</button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};
export default BillHistoryPage;
